/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useMemo, useRef, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { SNMPDevice, SNMPInterface, getSNMPInterfaceHistory } from "@/api/snmp";
import { SNMP_REFRESH_INTERVALS } from "@/constants/snmpRefreshIntervals";
import { useSNMPDevice } from "@/hooks/useSNMPDevice";
import { toTrafficPoints } from "@/utils/snmpTraffic";
import { SNMPTrafficChart, TrafficPoint } from "../SNMPTrafficChart";
import { KV, Spark, StatusDot, Tag, fmtBits, fmtRate } from "@/components/servers/kit";
import { SkeletonRows } from "@/components/common/States";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";
import { cn } from "@/lib/utils";

const OPER_UP = 1;

const adminLabel = (status: number | undefined) => (status === 1 ? "Enabled" : status === 2 ? "Disabled" : status === 3 ? "Testing" : "Unknown");
const linkLabel = (status: number | undefined) => (status === 1 ? "Link up" : status === 2 ? "Link down" : status === 3 ? "Testing" : "Unknown");

/** Short link speed for the faceplate (ifSpeed is bits per second). */
const speedShort = (bps: number): string => {
  if (!bps || bps <= 0) return "";
  if (bps >= 1e9) return `${+(bps / 1e9).toFixed(1)}G`;
  return `${Math.round(bps / 1e6)}M`;
};

const errorCount = (i: SNMPInterface) =>
  (i.latest?.inErrors ?? 0) + (i.latest?.outErrors ?? 0) + (i.latest?.inDiscards ?? 0) + (i.latest?.outDiscards ?? 0);

const portName = (i: SNMPInterface) => i.ifAlias || i.ifDescr || `Port ${i.ifIndex}`;

type PortFilter = "all" | "up" | "down" | "errors";
const FILTERS: [PortFilter, string][] = [
  ["all", "All"],
  ["up", "Up"],
  ["down", "Down"],
  ["errors", "Errors"],
];
const HISTORY_WINDOWS = [60, 120, 240] as const;

/** Rates in the units the device reports: some devices (the web-scrape shim) count packets, not bytes. */
interface Units {
  packets: boolean;
  rate: (v: number) => string;
}
const bytesUnits: Units = { packets: false, rate: (v) => fmtRate(v) };
const fmtPackets = (v: number) =>
  v >= 1e6 ? `${(v / 1e6).toFixed(v >= 1e7 ? 0 : 1)}M pkt/s` : v >= 1000 ? `${(v / 1000).toFixed(v >= 1e4 ? 0 : 1)}k pkt/s` : `${Math.round(v)} pkt/s`;
const packetUnits: Units = { packets: true, rate: fmtPackets };

interface PortRow {
  iface: SNMPInterface;
  up: boolean;
  errors: number;
  points: TrafficPoint[];
  inRate: number;
  outRate: number;
}

const lastRate = (points: TrafficPoint[], key: "in" | "out") => (points.length ? points[points.length - 1][key] : 0);

interface SNMPPortsTabProps {
  device: SNMPDevice;
}

export const SNMPPortsTab: React.FC<SNMPPortsTabProps> = ({ device }) => {
  const [filter, setFilter] = useState<PortFilter>("all");
  const [query, setQuery] = useState("");
  const [historyLimit, setHistoryLimit] = useState<number>(120);
  const [openId, setOpenId] = useState<number | null>(null);
  const rowRefs = useRef(new Map<number, HTMLDivElement>());

  const { interfaces = [], status, isLoadingInterfaces } = useSNMPDevice({ device });
  const units = status?.sysDescr?.includes("packet counts") ? packetUnits : bytesUnits;

  const upIfaces = useMemo(() => interfaces.filter((i) => i.latest?.operStatus === OPER_UP), [interfaces]);

  // One history query per live port, owned here so the summary tiles and the rows share the data.
  const histories = useQueries({
    queries: upIfaces.map((i) => ({
      queryKey: ["snmp-interface-history", device.id, i.id, historyLimit],
      queryFn: () => getSNMPInterfaceHistory(device.id, i.id, 1, historyLimit),
      refetchInterval: SNMP_REFRESH_INTERVALS.HISTORY,
    })),
  });

  const historyStamp = histories.map((h) => h.dataUpdatedAt).join(",");
  const rows: PortRow[] = useMemo(() => {
    const pointsById = new Map<number, TrafficPoint[]>();
    upIfaces.forEach((i, idx) => {
      const data = histories[idx]?.data;
      pointsById.set(i.id, data ? toTrafficPoints(data.data) : []);
    });
    return [...interfaces]
      .sort((a, b) => a.ifIndex - b.ifIndex)
      .map((iface) => {
        const points = pointsById.get(iface.id) ?? [];
        return {
          iface,
          up: iface.latest?.operStatus === OPER_UP,
          errors: errorCount(iface),
          points,
          inRate: lastRate(points, "in"),
          outRate: lastRate(points, "out"),
        };
      });
    // `histories` is a new array every render; the timestamps say when its data changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interfaces, upIfaces, historyStamp]);

  if (isLoadingInterfaces) return <SkeletonRows rows={5} />;

  const upCount = rows.filter((r) => r.up).length;
  const errPorts = rows.filter((r) => r.errors > 0).length;
  const totalIn = rows.reduce((s, r) => s + r.inRate, 0);
  const totalOut = rows.reduce((s, r) => s + r.outRate, 0);
  const busiest = rows.reduce<PortRow | null>((best, r) => (r.up && (!best || r.inRate + r.outRate > best.inRate + best.outRate) ? r : best), null);

  const q = query.trim().toLowerCase();
  const visible = rows.filter((r) => {
    if (filter === "up" && !r.up) return false;
    if (filter === "down" && r.up) return false;
    if (filter === "errors" && r.errors === 0) return false;
    if (q && !`${portName(r.iface)} ${r.iface.ifDescr} ${r.iface.ifIndex}`.toLowerCase().includes(q)) return false;
    return true;
  });
  type Group = { key: string; title: string; tone: "warn" | "ok" | "idle"; items: PortRow[] };
  const groups: Group[] = (
    [
      { key: "err", title: "With errors", tone: "warn", items: visible.filter((r) => r.up && r.errors > 0) },
      { key: "up", title: "Up", tone: "ok", items: visible.filter((r) => r.up && r.errors === 0) },
      { key: "down", title: "Down", tone: "idle", items: visible.filter((r) => !r.up) },
    ] as Group[]
  ).filter((g) => g.items.length > 0);

  const select = (id: number) => {
    const next = openId === id ? null : id;
    setOpenId(next);
    if (next !== null) requestAnimationFrame(() => rowRefs.current.get(id)?.scrollIntoView({ block: "nearest", behavior: "smooth" }));
  };

  return (
    <div className="space-y-3">
      <div className="sw-sum">
        <div className={cn("sw-kpi", upCount === rows.length && rows.length > 0 && "sw-kpi--ok")}>
          <small>Ports up</small>
          <b>
            <AnimatedNumber value={upCount} mountFrom={null} />
            <i>{` / ${rows.length}`}</i>
          </b>
        </div>
        <div className="sw-kpi">
          <small>{units.packets ? "Packets now" : "Traffic now"}</small>
          <b className="sw-kpi-rate">
            <span>{`↓ ${units.rate(totalIn)}`}</span>
            <span>{`↑ ${units.rate(totalOut)}`}</span>
          </b>
        </div>
        <div className="sw-kpi">
          <small>Busiest port</small>
          <b className="sw-kpi-name">{busiest && busiest.inRate + busiest.outRate > 0 ? portName(busiest.iface) : "-"}</b>
        </div>
        <div className={cn("sw-kpi", errPorts > 0 ? "sw-kpi--warn" : "sw-kpi--ok")}>
          <small>Ports with errors</small>
          <b>
            <AnimatedNumber value={errPorts} mountFrom={null} />
          </b>
        </div>
      </div>

      <Faceplate rows={rows} openId={openId} onSelect={select} />

      <div className="sw-bar">
        <input
          className="sw-filter"
          type="search"
          placeholder="Find a port…"
          aria-label="Find a port"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-3">
          <div className="sw-seg" role="group" aria-label="History window" title="History window (data points per port)">
            {HISTORY_WINDOWS.map((w) => (
              <button key={w} type="button" aria-pressed={historyLimit === w} onClick={() => setHistoryLimit(w)}>
                {w}
              </button>
            ))}
          </div>
          <div className="sw-seg" role="group" aria-label="Filter ports">
            {FILTERS.map(([f, label]) => (
              <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="sw-empty">No ports match this filter.</div>
      ) : (
        <div className="sw-table sw-ptable">
          <div className="sw-prow sw-thead" role="row">
            <span>Port</span>
            <span>Status</span>
            <span className="sw-num">Inbound</span>
            <span className="sw-num sw-out">Outbound</span>
            <span className="sw-pspark">Traffic</span>
            <span className="sw-num sw-err">Errors</span>
          </div>
          {groups.map((g) => (
            <React.Fragment key={g.key}>
              <div className={cn("sw-grp", `sw-grp--${g.tone}`)}>
                <b>{g.title}</b>
                <span>{g.items.length}</span>
              </div>
              {g.items.map((r) => (
                <div
                  key={r.iface.id}
                  ref={(el) => {
                    if (el) rowRefs.current.set(r.iface.id, el);
                    else rowRefs.current.delete(r.iface.id);
                  }}
                >
                  <PortLine row={r} open={openId === r.iface.id} units={units} onToggle={() => select(r.iface.id)} />
                  {openId === r.iface.id && <PortDetail row={r} units={units} />}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

/** The switch's front panel: one jack per port, odd ports on top like on the real device. */
const Faceplate: React.FC<{ rows: PortRow[]; openId: number | null; onSelect: (id: number) => void }> = ({ rows, openId, onSelect }) => {
  if (rows.length === 0) return null;
  const single = rows.length <= 8;
  return (
    <div className="sw-face">
      <div className="sw-face-head">
        <span className="sw-face-title">{`${rows.length} ports`}</span>
        <span className="sw-face-legend">
          <span><i className="sw-led sw-led--up" />Up</span>
          <span><i className="sw-led sw-led--err" />Errors</span>
          <span><i className="sw-led sw-led--down" />Down</span>
        </span>
      </div>
      <div className={cn("sw-jacks", single && "sw-jacks--single")}>
        {rows.map((r) => {
          const state = !r.up ? "down" : r.errors > 0 ? "err" : "up";
          return (
            <button
              key={r.iface.id}
              type="button"
              className={cn("sw-jack", `sw-jack--${state}`, openId === r.iface.id && "sw-jack--sel")}
              aria-label={`${portName(r.iface)}: ${linkLabel(r.iface.latest?.operStatus)}`}
              aria-pressed={openId === r.iface.id}
              title={`${portName(r.iface)} · ${linkLabel(r.iface.latest?.operStatus)}${r.iface.ifSpeed ? ` · ${fmtBits(r.iface.ifSpeed / 8)}` : ""}`}
              onClick={() => onSelect(r.iface.id)}
            >
              <b>{r.iface.ifIndex}</b>
              <i className={cn("sw-led", `sw-led--${state}`)} />
              <small>{r.up ? speedShort(r.iface.ifSpeed) : ""}</small>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const PortLine: React.FC<{ row: PortRow; open: boolean; units: Units; onToggle: () => void }> = ({ row, open, units, onToggle }) => {
  const { iface, up, errors, points } = row;
  const speed = iface.ifSpeed > 0 ? fmtBits(iface.ifSpeed / 8) : "";
  const peak = Math.max(1, ...points.flatMap((p) => [p.in, p.out]));
  return (
    <button type="button" className={cn("sw-prow", !up && "sw-prow--down", open && "sw-trow--sel")} aria-expanded={open} onClick={onToggle}>
      <span className="sw-name">
        <StatusDot state={!up ? "idle" : errors > 0 ? "warn" : "up"} />
        <span>
          <b title={portName(iface)}>{portName(iface)}</b>
          <small>{`ifIndex ${iface.ifIndex}`}</small>
        </span>
      </span>
      <span className="sw-pstate">
        <Tag tone={up ? "ok" : ""}>{linkLabel(iface.latest?.operStatus)}</Tag>
        {up && speed && <small>{speed}</small>}
      </span>
      <span className="sw-num">{up ? units.rate(row.inRate) : "-"}</span>
      <span className="sw-num sw-out">{up ? units.rate(row.outRate) : "-"}</span>
      <span className="sw-pspark">
        {up && points.length > 1 ? (
          <Spark
            height={24}
            legend={false}
            max={peak}
            series={[
              { values: points.map((p) => p.in), color: "var(--gh-signal)" },
              { values: points.map((p) => p.out), color: "var(--gh-dim)" },
            ]}
          />
        ) : (
          <span className="sw-pspark-empty" />
        )}
      </span>
      <span className="sw-num sw-err">{errors > 0 ? <Tag tone="warn">{String(errors)}</Tag> : up ? "0" : "-"}</span>
    </button>
  );
};

const PortDetail: React.FC<{ row: PortRow; units: Units }> = ({ row, units }) => {
  const { iface, up, points } = row;
  const l = iface.latest;
  return (
    <div className="sw-pdetail">
      <div className="sw-pdetail-chart">
        {up ? (
          <SNMPTrafficChart data={points} height={150} packets={units.packets} />
        ) : (
          <div className="sw-empty">This port is down, so there is no live traffic.</div>
        )}
      </div>
      <div className="sw-pdetail-kv sv-kv">
        <KV k="Admin status">{adminLabel(l?.adminStatus)}</KV>
        <KV k="Link status">{linkLabel(l?.operStatus)}</KV>
        <KV k="Link speed">{iface.ifSpeed > 0 ? fmtBits(iface.ifSpeed / 8) : "-"}</KV>
        <KV k="Interface">{`${iface.ifDescr || "-"} · ifIndex ${iface.ifIndex}`}</KV>
        <KV k={units.packets ? "In / out packets" : "In / out total"}>
          {l ? `${l.inOctets.toLocaleString()} / ${l.outOctets.toLocaleString()}` : "-"}
        </KV>
        <KV k="Errors in / out">{`${l?.inErrors ?? 0} / ${l?.outErrors ?? 0}`}</KV>
        <KV k="Discards in / out">{`${l?.inDiscards ?? 0} / ${l?.outDiscards ?? 0}`}</KV>
      </div>
    </div>
  );
};
